import React from "react";
import classNames from "classnames";
import { Text } from "@jsq/ds/core";
import styles from "./styles.scss";

type PasswordResetBannerPropsInput = {
  showPasswordResetSuccessBanner?: boolean;
  passwordResetEmail?: string;
};

/**
 * Get appropriate props to pass into our DS banner component for the password
 * reset flow on the Portal 2.0 login page
 */
export const getPasswordResetBannerProps = (objectInput: PasswordResetBannerPropsInput) => {
  if (objectInput.showPasswordResetSuccessBanner) {
    return {
      className: "stacked-md",
      variant: "success",
      title: "Password reset successful",
      description: "",
      isDescriptionJsx: false,
    };
  }
  return null;
};

type PortalBannerPropsInput = {
  showContactSupportBanner?: boolean;
  showHasManagerAccountBanner?: boolean;
  showActivationLinkBanner?: boolean;
  activationLink?: string;
  showHasActiveGlobalAccountBanner?: boolean;
  currentGpArenaName?: string;
  portalLinkedGpArenaNames?: string;
  gpContactEmail?: string | null;
  gpContactPhone?: string | null;
};
/**
 * Get appropriate props to pass into our DS banner component for users being redirected
 * from the Portal 2.0 activation flow to the Portal 2.0 login page (primarily because the
 * user already has a Portal 2.0 account that they can use to log into the product)
 */
export const getPortalBannerProps = (objectInput: PortalBannerPropsInput) => {
  if (objectInput.showContactSupportBanner) {
    let description;
    if (objectInput.gpContactEmail && objectInput.gpContactPhone) {
      description = (
        <Text variant="regular-md" className={styles.LoginForm__activationBannerDescription}>
          Please contact us at{" "}
          <a href={`mailto:${objectInput.gpContactEmail}`}>{objectInput.gpContactEmail}</a> or{" "}
          {objectInput.gpContactPhone} for a portal invitation.
        </Text>
      );
    } else if (objectInput.gpContactEmail) {
      description = (
        <Text variant="regular-md" className={styles.LoginForm__activationBannerDescription}>
          Please contact us at{" "}
          <a href={`mailto:${objectInput.gpContactEmail}`}>{objectInput.gpContactEmail}</a> for
          a portal invitation.
        </Text>
      );
    } else if (objectInput.gpContactPhone) {
      description = (
        <Text variant="regular-md" className={styles.LoginForm__activationBannerDescription}>
          Please contact us at {objectInput.gpContactPhone} for a portal invitation.
        </Text>
      );
    } else {
      description = (
        <Text variant="regular-md" className={styles.LoginForm__activationBannerDescription}>
          Please contact us for a portal invitation.
        </Text>
      );
    }
    return {
      className: "stacked-md",
      variant: "primary",
      title: "Trying to activate your investor portal account?",
      description,
      isDescriptionJsx: true,
    };
  } else if (objectInput.showHasManagerAccountBanner) {
    return {
      className: classNames("stacked-md", "form-message"),
      variant: "primary",
      title: "Use your Juniper Square account",
      description:
        "Looks like you already have a Juniper Square manager account. Please use the same " +
        "credentials to sign in.",
      isDescriptionJsx: false,
    };
  } else if (objectInput.showActivationLinkBanner && objectInput.activationLink) {
    return {
      className: "stacked-md",
      variant: "primary",
      title: "First time here?",
      description: (
        <Text variant="regular-md" className={styles.LoginForm__activationBannerDescription}>
          You will need to activate your account to continue.{" "}
          <b>
            <a href={objectInput.activationLink}>Activate account</a>
          </b>
        </Text>
      ),
      isDescriptionJsx: true,
    };
  } else if (
    objectInput.showHasActiveGlobalAccountBanner &&
    objectInput.currentGpArenaName &&
    objectInput.portalLinkedGpArenaNames
  ) {
    return {
      className: "stacked-md",
      variant: "primary",
      title: "Use your Juniper Square account",
      description:
        "You have access to multiple investor portals that are powered by Juniper Square. " +
        `Sign in to ${objectInput.currentGpArenaName}'s portal using the same credentials that you use for ${objectInput.portalLinkedGpArenaNames}.`,
      isDescriptionJsx: false,
    };
  }
  return null;
};

/*
 * infoBanner is displayed above the login form and may be used to display a banner when re-routing to the
 * login page.
 * Options:
 *   invalid_token - Used to indicate the GP account activation token has already been used. LP account activation
 *                   token is managed by the shouldRedirectToLogin param, not invalid_token
 * */
export const getInfoBannerProps = (infoBanner: string | null) => {
  if (infoBanner === "invite_token_already_used") {
    return {
      title: "This account has already been activated",
      description: "Sign in using your email address and password.",
    };
  } else if (infoBanner === "invite_token_invalid") {
    return {
      title: "This activation link is invalid",
      description: "Please contact support for assistance",
    };
  } else if (infoBanner === "incorrect_login_page") {
    return {
      title: "Sign in here to access your investor portal",
      description: "To access your investor portal, sign in using your Juniper Square account",
    };
  }
  return {
    title: "Something went wrong",
    description: "Please try again.",
  };
};
